import React, { cloneElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    GestureResponderEvent,
    LayoutChangeEvent,
    PixelRatio,
    Platform,
    Pressable,
    StyleProp,
    StyleSheet,
    TextStyle,
    View,
    ViewStyle,
} from "react-native";
import { useTheme } from "../hooks/use-theme";
import rnTextSize, { TSFontSpecs } from "react-native-text-size";
import Animated, {
    runOnJS,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from "react-native-reanimated";
import Icon, { hasIcon } from "./Icon";
import Text from "./Text";
import mergeRefs from "merge-refs";
import { isString } from "lodash";

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const FONT_SCALE = PixelRatio.getFontScale();

// When label is in field (normal state)
const LABEL_BASE_TOP = 16;

// When label is at the top of the field (focused state)
const LABEL_FOCUSED_TOP = -8.5;

// Value when isTop is equals to
// [true, false]
const DEFAULT_TOP = [
    LABEL_BASE_TOP - (FONT_SCALE - 1) * (LABEL_BASE_TOP / 2),
    LABEL_FOCUSED_TOP * FONT_SCALE,
];

const FIELD_BORDER_WIDTH = 2;

export const FIELD_HEIGHT = 54;

const LABEL_FONT_SIZE = 12;

// Value when isTop is equals to
// [true, false]
const LEFT_ANIMATED = [8, 6];
const FONT_SIZE_ANIMATED = [17, LABEL_FONT_SIZE];
const FONT_FAMILY_ANIMATED = ["Inter-Regular", "Inter-SemiBold"];

const FONT_TOP_STYLE: TSFontSpecs = {
    fontFamily: FONT_FAMILY_ANIMATED[1],
    fontSize: FONT_SIZE_ANIMATED[1],
    includeFontPadding: false,
};

const FONT_BOTTOM_STYLE: TSFontSpecs = {
    fontFamily: FONT_FAMILY_ANIMATED[0],
    fontSize: FONT_SIZE_ANIMATED[0],
    includeFontPadding: false,
};

const ANIMATION_DURATION = 200;

const DEFAULT_ICON_SIZE = 22;

type LabelTextProps = {
    label: string;
    isPlaceholder?: boolean;
    forceTransparent?: boolean;
    labelLeftPadding?: number;
    labelTopPadding?: number;
    isTop: boolean;
    isFocused: boolean;
    style?: StyleProp<TextStyle>;
    isSelfContained?: boolean;
    forceDefaultTop?: boolean;
    labelWidths: number[];
};

const useFieldColor = () => {
    const { colors } = useTheme();

    return useMemo(() => [colors.fieldLabel, colors.primary], [colors.fieldLabel, colors.primary]);
};

const LabelText = ({
    label,
    isPlaceholder = false,
    forceTransparent = false,
    labelLeftPadding = 0,
    labelTopPadding = 0,
    isTop,
    isFocused,
    style,
    isSelfContained,
    forceDefaultTop,
    labelWidths,
}: LabelTextProps) => {
    const styles = useStyles();
    const { marginSize } = useTheme();
    const horizontalPadding = useMemo(() => marginSize / 2, [marginSize]);

    const flattenStyle = StyleSheet.flatten(style);
    const colorAnimated = useFieldColor();
    const [initialLabelHeight, setInitialLabelHeight] = useState(0);

    const getTop = useCallback(
        (_initialLabelHeight?: number) =>
            isTop
                ? isSelfContained
                    ? 6
                    : DEFAULT_TOP[+isTop]
                : (forceDefaultTop
                      ? DEFAULT_TOP[+isTop]
                      : _initialLabelHeight || initialLabelHeight) + labelTopPadding,
        [forceDefaultTop, initialLabelHeight, isSelfContained, isTop, labelTopPadding],
    );

    const hasFixedWidths = useMemo(() => labelWidths[0] > 0, [labelWidths]);

    const top = useSharedValue(0);
    const width = useSharedValue(labelWidths[+isTop]);
    const color = useSharedValue(flattenStyle.color || colorAnimated[+isFocused]);
    const left = useSharedValue(isTop ? LEFT_ANIMATED[1] : LEFT_ANIMATED[0] + labelLeftPadding);
    const fontSize = useSharedValue(FONT_SIZE_ANIMATED[+isTop]);
    const opacity = useSharedValue(isTop || forceTransparent ? 0 : 1);
    const animatedStyles = useAnimatedStyle(() => ({
        color: color.value,
        left: left.value,
        opacity: opacity.value,
        ...(initialLabelHeight > 0
            ? { fontSize: fontSize.value, top: top.value }
            : { fontSize: FONT_SIZE_ANIMATED[+false] }),
        ...(hasFixedWidths ? { width: width.value } : {}),
    }));

    useEffect(() => {
        opacity.value = withTiming(forceTransparent ? 0.0 : 1.0, {
            duration: ANIMATION_DURATION,
        });
    }, [forceTransparent, opacity]);

    useEffect(() => {
        left.value = withTiming(
            isTop && !isPlaceholder ? LEFT_ANIMATED[1] : LEFT_ANIMATED[0] + labelLeftPadding,
            {
                duration: ANIMATION_DURATION,
            },
        );
        if (!isPlaceholder) {
            color.value = withTiming((flattenStyle.color as string) || colorAnimated[+isFocused], {
                duration: ANIMATION_DURATION,
            });
            top.value = withTiming(getTop(), {
                duration: ANIMATION_DURATION,
            });
            fontSize.value = withTiming(FONT_SIZE_ANIMATED[+isTop], {
                duration: ANIMATION_DURATION,
            });
        }

        if (hasFixedWidths) {
            width.value = withTiming(
                labelWidths[+isTop] + horizontalPadding * 2 - (!isTop ? labelLeftPadding : 0),
                {
                    duration: width.value > 0 ? ANIMATION_DURATION : 0,
                },
            );
        }
    }, [
        isTop,
        fontSize,
        left,
        labelLeftPadding,
        isPlaceholder,
        color,
        colorAnimated,
        flattenStyle.color,
        isFocused,
        labelTopPadding,
        isSelfContained,
        getTop,
        top,
        width,
        labelWidths,
        horizontalPadding,
        hasFixedWidths,
    ]);

    const onLayout = useCallback(
        (event: LayoutChangeEvent) => {
            if (initialLabelHeight === 0 && !isPlaceholder) {
                const newTop = event.nativeEvent.layout.y - FIELD_BORDER_WIDTH;
                setInitialLabelHeight(newTop);
                top.value = withTiming(getTop(newTop), {
                    duration: 0,
                });
                setTimeout(() => {
                    opacity.value = withTiming(1, {
                        duration: 0,
                    });
                }, 50); // 50 is necessary to avoid label to not appear on iOS
            }
        },
        [getTop, initialLabelHeight, isPlaceholder, opacity, top],
    );

    return (
        <Animated.Text
            numberOfLines={1}
            onLayout={onLayout}
            style={[
                styles.labelText,
                style,
                animatedStyles,
                {
                    fontFamily: FONT_FAMILY_ANIMATED[+(isTop && !isPlaceholder)],
                    paddingHorizontal: horizontalPadding,
                },
                isPlaceholder && { backgroundColor: "transparent" },
            ]}
        >
            {label}
        </Animated.Text>
    );
};

type BadgeType = string | React.ReactElement;

type FocusRef = {
    focus?: (e: GestureResponderEvent) => void;
};

export type BaseFieldProps = {
    component: React.ReactElement<{
        onFocus?: () => void;
        onBlur?: () => void;
        isFocused?: boolean;
        style?: StyleProp<ViewStyle>;
        ref: React.Ref<FocusRef> | React.RefCallback<FocusRef>;
    }>;
    componentRef?: React.Ref<FocusRef>;
    label?: string;
    placeholder?: string;
    labelLeftPadding?: number;
    labelTopPadding?: number;
    style?: StyleProp<ViewStyle>;
    left?: BadgeType;
    leftColor?: string;
    right?: BadgeType;
    rightColor?: string;
    disabled?: boolean;
    error?: boolean | string;
    showErrorText?: boolean;
    value?: string | boolean;
    valueColor?: string;
    onFocus?: () => void;
    onBlur?: () => void;
    testID?: string;
    inputStyle?: StyleProp<TextStyle>;
    asListAction?: "primary" | "secondary"; // field as L&F of a list action
    allowFontScaling?: boolean;
    numberOfLines?: number;
    isFocused?: boolean;
    isSelfContained?: boolean;
    asButton?: boolean;
    info?: React.ReactNode;
};

const BaseField = ({
    label,
    placeholder,
    value,
    valueColor,
    component,
    componentRef,
    style,
    left,
    isSelfContained = false,
    labelLeftPadding = 0,
    labelTopPadding = 0,
    right,
    leftColor,
    rightColor,
    disabled,
    error,
    showErrorText = true,
    onFocus,
    onBlur,
    testID,
    inputStyle,
    asListAction,
    allowFontScaling,
    numberOfLines = undefined,
    isFocused = false,
    asButton = false,
    info = undefined,
}: BaseFieldProps) => {
    const { colors, marginSize, gridSize } = useTheme();
    const horizontalPadding = useMemo(() => marginSize / 2, [marginSize]);
    const styles = useStyles();

    const [isPlaceholderTop, setIsPlaceholderTop] = useState(!!value);
    const [isElevated, setIsElevated] = useState(false);

    const borderColorAnimated = useMemo(
        () => [colors.accentLighter, colors.primary],
        [colors.accentLighter, colors.primary],
    );

    const borderColor = useSharedValue(borderColorAnimated[+isFocused]);

    const animatedBorderColorStyles = useAnimatedStyle(() => ({
        borderColor: borderColor.value,
    }));

    const isBorderColorTransparent = useMemo(
        () => StyleSheet.flatten(style)?.borderColor === "transparent",
        [style],
    );

    const setElevatedFalse = useCallback(() => {
        setIsElevated(false);
    }, []);

    useEffect(() => {
        if (!isBorderColorTransparent) {
            borderColor.value = withTiming(
                error ? colors.error : borderColorAnimated[+isFocused],
                { duration: ANIMATION_DURATION },
                () => {
                    if (!isFocused) {
                        runOnJS(setElevatedFalse)();
                    }
                },
            );
        }

        if (isFocused) {
            setTimeout(() => {
                setIsElevated(true);
            }, 1);
            setIsPlaceholderTop(true);
        } else {
            if (!value) {
                setIsPlaceholderTop(false);
            }
        }
    }, [
        borderColor,
        borderColorAnimated,
        colors.error,
        error,
        isBorderColorTransparent,
        isFocused,
        label,
        setElevatedFalse,
        style,
        value,
    ]);

    useEffect(() => {
        if (!isFocused) {
            setIsPlaceholderTop(!!value);
        }
        // To avoid triggering the useEffect 1. on every value's change, 2. if isFocused changes, because it is already handled above
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [!!value]);

    const isTextField = useMemo(() => typeof value !== "string", [value]);

    const errorComponent = useMemo(
        () =>
            !!error &&
            showErrorText &&
            isString(error) && (
                <Text
                    style={[
                        styles.errorText,
                        isSelfContained && {
                            marginTop: -10,
                            marginHorizontal: 0,
                        },
                    ]}
                >
                    {error}
                </Text>
            ),
        [error, isSelfContained, showErrorText, styles.errorText],
    );

    const [leftBadgeWidth, setLeftBadgeWidth] = useState(0);

    const onLeftBadgeLayout = useCallback(
        (event: LayoutChangeEvent) => {
            setLeftBadgeWidth(event.nativeEvent.layout.width + marginSize / 2);
        },
        [marginSize],
    );

    const [labelWidths, setLabelWidths] = useState([0, 0]);

    const onFieldLayout = useCallback(
        async (e: LayoutChangeEvent) => {
            if (!label || Platform.OS === "web") {
                return;
            }
            const { width: maxWidth } = e.nativeEvent.layout;

            const baseFieldMaxWidth = maxWidth - horizontalPadding * 2 - 2 * FIELD_BORDER_WIDTH;

            const fieldMaxTopWidth = baseFieldMaxWidth - LEFT_ANIMATED[1] * 2;
            const fieldMaxBottomWidth = baseFieldMaxWidth - LEFT_ANIMATED[0] * 2;

            try {
                const promises = [];

                promises.push(rnTextSize.measure({ text: label, ...FONT_TOP_STYLE }));
                promises.push(rnTextSize.measure({ text: label, ...FONT_BOTTOM_STYLE }));

                const [{ width: labelTopWidth }, { width: labelBottomWidth }] =
                    await Promise.all(promises);

                // Only set fixed widths when the label is longer than
                if (labelBottomWidth > fieldMaxBottomWidth) {
                    setLabelWidths([
                        fieldMaxBottomWidth,
                        labelTopWidth > fieldMaxTopWidth ? fieldMaxTopWidth : labelTopWidth,
                    ]);
                }
            } catch (e) {
                console.error(e);
            }
        },
        [horizontalPadding, label],
    );

    const labelOrPlaceHolder = label || placeholder;

    const ref = useRef<FocusRef>(null);

    return (
        <>
            <AnimatedPressable
                testID={testID}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onLayout={onFieldLayout}
                style={[
                    styles.field,
                    asListAction && styles.viewAsListAction,
                    animatedBorderColorStyles,
                    style,
                    isElevated && { zIndex: 1 },
                    Platform.OS === "web" && !disabled && { cursor: asButton ? "pointer" : "text" },
                ]}
                onPress={(e) => {
                    if (!disabled) {
                        ref.current?.focus?.(e);
                    }
                }}
            >
                {labelOrPlaceHolder && (
                    <LabelText
                        label={labelOrPlaceHolder}
                        labelLeftPadding={(leftBadgeWidth || 0) + labelLeftPadding}
                        labelTopPadding={labelTopPadding}
                        labelWidths={labelWidths}
                        isTop={isPlaceholderTop && !placeholder}
                        isFocused={isFocused}
                        style={[
                            asListAction ? styles.listActionText : {},
                            asListAction === "primary" && styles.listActionTextPrimary,
                            asListAction === "secondary" && styles.listActionTextSecondary,
                            error ? { color: colors.error } : {},
                            /* Not sure about this one, if not set the placeholder is displayed on the bottom */
                            info && !isPlaceholderTop ? { top: gridSize / 1.25 } : {},
                        ]}
                        isPlaceholder={!!placeholder}
                        forceTransparent={
                            !!placeholder && ![false, null, undefined, ""].includes(value)
                        }
                        isSelfContained={isSelfContained}
                        forceDefaultTop={
                            ((StyleSheet.flatten(style)?.height as number) || 0) > FIELD_HEIGHT
                        }
                    />
                )}
                <View
                    style={{
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "row",
                        minHeight: FIELD_HEIGHT,
                        paddingTop: gridSize / 2,
                        paddingBottom: info ? 0 : gridSize / 2,
                        // used in web to be sure that the component is full size
                        ...(Platform.OS === "web"
                            ? {
                                  height: "100%",
                                  width: "100%",
                                  paddingVertical: 0,
                              }
                            : {}),
                    }}
                >
                    {left && (
                        <View style={[styles.badge, { paddingRight: marginSize / 2 }]}>
                            <Badge
                                color={leftColor || colors.primary}
                                badge={left}
                                error={!!error}
                                disabled={disabled}
                                onLayout={onLeftBadgeLayout}
                            />
                        </View>
                    )}
                    {!isTextField && (
                        <Text
                            numberOfLines={numberOfLines}
                            style={[
                                styles.valueText,
                                isSelfContained && styles.selfContainText,
                                disabled && styles.disabledValue,
                                {
                                    ...(asListAction === "primary"
                                        ? { color: colors.secondary }
                                        : asListAction === "secondary"
                                          ? { color: colors.onWarningBackground }
                                          : {}),
                                    ...(valueColor ? { color: valueColor } : {}),
                                },
                                inputStyle,
                            ]}
                            allowFontScaling={allowFontScaling}
                        >
                            {value}
                        </Text>
                    )}
                    {cloneElement(component, {
                        ref: mergeRefs(componentRef, ref),
                        ...(!asListAction
                            ? {
                                  onFocus,
                                  onBlur,
                                  isFocused,
                              }
                            : {}),
                        style: [
                            styles.valueText,
                            isSelfContained && styles.selfContainText,
                            {
                                flexGrow: 1,
                                flexShrink: 1,
                                height: "100%",
                                width: "100%",
                                ...(!isTextField
                                    ? {
                                          position: "absolute",
                                          zIndex: 100,
                                          justifyContent: "center",
                                      }
                                    : {}),
                            },
                            component.props.style, // set this to last to be able to override sizes
                        ],
                    })}
                    {!isTextField && <View style={{ flexGrow: 1 }} />}
                    {right && (
                        <View>
                            <Badge
                                color={
                                    rightColor ||
                                    (asListAction === "primary"
                                        ? colors.secondary
                                        : asListAction === "secondary"
                                          ? colors.onWarningBackground
                                          : colors.primary)
                                }
                                badge={right}
                                error={!!error}
                                disabled={disabled}
                                {...(asListAction ? { size: 18 } : {})}
                            />
                        </View>
                    )}
                </View>
                {info && <View style={{ paddingBottom: gridSize / 2 }}>{info}</View>}
                {isSelfContained && errorComponent}
            </AnimatedPressable>
            {!isSelfContained && errorComponent}
        </>
    );
};

type BadgeProps = {
    badge: BadgeType;
    error?: boolean;
    disabled?: boolean;
    color?: string;
    size?: number;
    onLayout?: (event: LayoutChangeEvent) => void;
};

const Badge = ({
    badge,
    error,
    disabled,
    color,
    size = DEFAULT_ICON_SIZE,
    onLayout,
}: BadgeProps) => {
    const { colors } = useTheme();

    return typeof badge === "string" ? (
        hasIcon(badge) ? (
            <Icon
                name={badge}
                size={size}
                onLayout={onLayout}
                {...(error
                    ? { color: colors.error }
                    : disabled
                      ? { color: colors.backdrop }
                      : { color: color || colors.primary })}
            />
        ) : (
            <Text
                onLayout={onLayout}
                style={{ color: color || (error ? colors.error : undefined) }}
            >
                {badge}
            </Text>
        )
    ) : (
        badge
    );
};

const useStyles = () => {
    const { roundness, colors, marginSize, fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                field: {
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    flexGrow: 1,
                    borderWidth: FIELD_BORDER_WIDTH,
                    borderRadius: roundness,
                    marginTop: 4, // This is to allow label to be displayed correctly when the field is used with overflow hidden
                    paddingHorizontal: marginSize,
                },
                viewAsListAction: {
                    marginTop: 0,
                    borderWidth: 0,
                    borderBottomWidth: 1,
                    borderColor: colors.accentLighter,
                    borderRadius: 0,
                },
                labelText: {
                    position: "absolute",
                    backgroundColor: colors.background,
                    includeFontPadding: false,
                },
                selfContainText: {
                    top: 8,
                },
                valueText: {
                    ...fonts.bodyMedium,
                    textAlignVertical: "center",
                    backgroundColor: "transparent",
                    fontSize: FONT_SIZE_ANIMATED[0],
                    flexShrink: 1,
                    flexWrap: "wrap",
                    width: "100%",
                },
                disabledValue: {
                    color: colors.fieldLabel,
                },
                badge: {
                    color: colors.secondary,
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1,
                },
                errorText: {
                    color: colors.error,
                    width: "100%",
                    fontSize: LABEL_FONT_SIZE,
                    marginVertical: marginSize / 4,
                    marginHorizontal: 4,
                },
                listActionText: {
                    ...fonts.bodyMedium,
                },
                listActionTextPrimary: {
                    color: colors.secondary,
                },
                listActionTextSecondary: {
                    color: colors.onWarningBackground,
                },
            }),
        [
            roundness,
            marginSize,
            colors.accentLighter,
            colors.background,
            colors.fieldLabel,
            colors.secondary,
            colors.error,
            colors.onWarningBackground,
            fonts.bodyMedium,
        ],
    );
};

export default BaseField;
